// @flow
import React, { Children, useEffect, useRef, type Node } from 'react';
import classnames from 'classnames';
import { throttle } from 'lodash';
import { forceCheck } from 'react-lazyload';
import { FocusOn, AutoFocusInside } from 'react-focus-on';

import { OVER_BOT } from 'Components/frameworks/portal/Portal.constants';
import { ICONS } from 'Components/utils/icon/icons';

import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import Portal from 'Components/frameworks/portal/Portal.jsx';

import { themes, POSITIONS_CLASSES } from './SidePanel.themes';

type Props = {
  ariaLabel?: string,
  ariaLabelledby?: string,
  backdropColor?: string,
  backdropOpacity?: number,
  children: Node,
  className?: string,
  closeLabel?: string,
  height?: string,
  isExpanded?: boolean,
  onClose?: (SyntheticEvent<HTMLElement>) => void,
  portalName?: string,
  position?: 'top' | 'right' | 'bottom' | 'left',
  theme?: string,
};

export function SidePanel(props: Props) {
  const {
    ariaLabel,
    ariaLabelledby,
    backdropColor = '#000',
    backdropOpacity = 0.8,
    children,
    className,
    closeLabel,
    height,
    isExpanded,
    onClose,
    portalName = OVER_BOT,
    position = 'bottom',
    theme = 'default',
  } = props;

  const sidePanelBodyref: { current: ?HTMLElement } = useRef();

  const onScroll = throttle(() => {
    forceCheck();
  }, 250);

  useEffect(() => {
    // $FlowFixMe
    sidePanelBodyref &&
      sidePanelBodyref.current &&
      sidePanelBodyref.current.addEventListener('scroll', onScroll);

    return () => {
      // $FlowFixMe
      sidePanelBodyref &&
        sidePanelBodyref.current &&
        sidePanelBodyref.current.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(
    () => {
      if (isExpanded) {
        forceCheck();
      }
    },
    [isExpanded],
  );

  if (!Children.count(children)) {
    return null;
  }

  const { thCloseButton, thCloseButtonLabel, thRoot } = themes[theme];
  const positionClasses = POSITIONS_CLASSES[position];
  const closeBtnSidePanel =
    closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : closeLabel;

  return (
    <Portal name={portalName}>
      <BodyLocker isLocked={isExpanded}>
        {isExpanded && (
          <div
            className="fadeIn fixed h-screen inset-0"
            data-testid="SidePanel-backdrop"
            style={{
              backgroundColor: backdropColor,
              opacity: backdropOpacity,
            }}
          />
        )}
        <div
          aria-hidden={!isExpanded}
          aria-label={ariaLabelledby ? null : ariaLabel}
          aria-labelledby={ariaLabelledby}
          role="dialog"
        >
          <div role="document">
            <FocusOn
              allowPinchZoom
              enabled={isExpanded}
              onClickOutside={onClose}
              onEscapeKey={onClose}
            >
              {isExpanded && (
                <AutoFocusInside>
                  <button
                    className={classnames('fixed z-2', thCloseButton, positionClasses.button)}
                    onClick={onClose}
                    type="button"
                  >
                    {closeBtnSidePanel && (
                      <span className={thCloseButtonLabel}>{closeBtnSidePanel}</span>
                    )}
                    <Icon name={ICONS.CROSS} width="1em" />
                  </button>
                </AutoFocusInside>
              )}

              <aside
                className={classnames(
                  className,
                  thRoot,
                  positionClasses.pin,
                  'fixed h-full overflow-y-scroll transition-all w-full z-1',
                  {
                    [positionClasses.transform]: !isExpanded,
                    'translate-0': isExpanded,
                  },
                )}
                ref={sidePanelBodyref}
                style={{ height, ...positionClasses.style }}
              >
                {isExpanded && children}
              </aside>
            </FocusOn>
          </div>
        </div>
      </BodyLocker>
    </Portal>
  );
}
